import React, { useState, useEffect, useRef } from 'react';
import { Board, boardApi, BoardMember, boardMemberApi, getBoardAccessDeniedMessage, clearBoardAccessDeniedMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EditPencil, Check, Xmark, Plus, Trash, BoxIso, NavArrowRight, NavArrowDown, Page, LogOut, ShieldCheck, Lock } from 'iconoir-react';
import { PendingInvitations } from './PendingInvitations';
import { AdBanner } from './AdBanner';
import { PremiumUpgradeBanner } from './PremiumUpgradeBanner';
import { InterstitialAd } from './InterstitialAd';
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan';
import { PremiumLimitModal } from './PremiumLimitModal';
import { ConfirmModal } from './ConfirmModal';

// Couleurs de base stockées en base de données
const COLORS = [
  '#5BC279', // Vert
  '#E5CA5C', // Jaune
  '#E5A333', // Orange
  '#E58D85', // Rouge/Rose
  '#B38EE5', // Violet
  '#66A8CC', // Bleu
  '#85CCDB', // Cyan
  '#A8C585', // Vert clair
  '#E591A8', // Rose
  '#8FA1A8', // Gris
];

const BOARD_FILTER_OPTIONS: { value: 'all' | 'owned' | 'shared'; label: string }[] = [
  { value: 'all', label: 'Tous les tableaux' },
  { value: 'owned', label: 'Mes tableaux' },
  { value: 'shared', label: 'Partagés avec moi' },
];

// Fonction pour convertir une couleur simple en dégradé
const getGradientFromColor = (color: string): string => {
  const gradientMap: Record<string, string> = {
    '#5BC279': 'linear-gradient(135deg, #5BC279 0%, #4FA368 100%)',
    '#E5CA5C': 'linear-gradient(135deg, #E5CA5C 0%, #C2AA4A 100%)',
    '#E5A333': 'linear-gradient(135deg, #E5A333 0%, #C28A2B 100%)',
    '#ea5d51ff': 'linear-gradient(135deg, #f95749 0%, #c64739ff 100%)',
    '#B38EE5': 'linear-gradient(135deg, #B38EE5 0%, #9678C2 100%)',
    '#66A8CC': 'linear-gradient(135deg, #56aeddff 0%, #147ab6ff 100%)',
    '#85CCDB': 'linear-gradient(135deg, #85CCDB 0%, #70ADB9 100%)',
    '#A8C585': 'linear-gradient(135deg, #A8C585 0%, #8DA670 100%)',
    '#E591A8': 'linear-gradient(135deg, #E591A8 0%, #C27A8D 100%)',
    '#8FA1A8': 'linear-gradient(135deg, #8FA1A8 0%, #78898F 100%)',
  };
  return gradientMap[color] || color;
};

export const BoardAdmin: React.FC = () => {
  const { user, logout } = useAuth();
  const { hasAds } = useSubscriptionPlan();
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingBoardId, setEditingBoardId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [showColorPalette, setShowColorPalette] = useState<number | null>(null);
  const [showInterstitialAd, setShowInterstitialAd] = useState(false);
  const [pendingBoardId, setPendingBoardId] = useState<number | null>(null);
  const [showPremiumLimitModal, setShowPremiumLimitModal] = useState(false);
  const [showOverageLockedModal, setShowOverageLockedModal] = useState(false);
  const [boardSearch, setBoardSearch] = useState('');
  const [boardFilter, setBoardFilter] = useState<'all' | 'owned' | 'shared'>('all');
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [boardToDeleteId, setBoardToDeleteId] = useState<number | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Lecture au render pour rester compatible avec React.StrictMode.
  const [accessDeniedMessage] = useState<string | null>(() => getBoardAccessDeniedMessage());

  useEffect(() => {
    loadBoards();
  }, []);

  useEffect(() => {
    if (accessDeniedMessage) {
      clearBoardAccessDeniedMessage();
    }
  }, [accessDeniedMessage]);

  useEffect(() => {
    if (!isFilterMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFilterMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFilterMenuOpen]);

  const loadBoards = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await boardApi.getAll();
      // La réponse contient maintenant { boards, has_overages, overages }
      const data: any = response.data;
      setBoards(Array.isArray(data) ? data : data.boards || []);
      
      // Afficher un avertissement si dépassement
      if (data.has_overages) {
        setError('Attention: Vous avez dépassé les limites de votre plan. Certains tableaux peuvent être inaccessibles.');
      }
    } catch (err: any) {
      setError('Erreur lors du chargement des tableaux');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMembers = async (boardId: number) => {
    try {
      const response = await boardMemberApi.getAll(boardId);
      setMembers(response.data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des membres', err);
    }
  };

  const handleSelectBoard = async (board: Board) => {
    setSelectedBoard(board);
    await loadMembers(board.id);
  };

  const handleUpdateColor = async (boardId: number, color: string) => {
    try {
      await boardApi.update(boardId, { background_color: color });
      await loadBoards();
      if (selectedBoard?.id === boardId) {
        setSelectedBoard({ ...selectedBoard, background_color: color });
      }
    } catch (err: any) {
      setError('Erreur lors de la mise à jour de la couleur');
    }
  };

  const handleStartEdit = (board: Board) => {
    setEditingBoardId(board.id);
    setEditTitle(board.title);
  };

  const handleSaveTitle = async (boardId: number) => {
    if (!editTitle.trim()) return;
    
    try {
      await boardApi.update(boardId, { title: editTitle.trim() });
      await loadBoards();
      if (selectedBoard?.id === boardId) {
        setSelectedBoard({ ...selectedBoard, title: editTitle.trim() });
      }
      setEditingBoardId(null);
    } catch (err: any) {
      setError('Erreur lors de la mise à jour du titre');
    }
  };

  const handleCancelEdit = () => {
    setEditingBoardId(null);
    setEditTitle('');
  };

  const handleCreateBoard = async () => {
    if (!newBoardTitle.trim()) return;
    
    setIsCreating(true);
    try {
      await boardApi.create({ title: newBoardTitle.trim() });
      setNewBoardTitle('');
      await loadBoards();
    } catch (err: any) {
      if (err.response?.status === 403) {
        setShowPremiumLimitModal(true);
      } else {
        setError('Erreur lors de la création du tableau');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBoard = (boardId: number) => {
    setBoardToDeleteId(boardId);
  };

  const handleConfirmDeleteBoard = async () => {
    const boardId = boardToDeleteId;
    setBoardToDeleteId(null);
    if (boardId === null) return;

    try {
      await boardApi.delete(boardId);
      if (selectedBoard?.id === boardId) {
        setSelectedBoard(null);
        setMembers([]);
      }
      await loadBoards();
    } catch (err: any) {
      setError('Erreur lors de la suppression du tableau');
    }
  };

  const handleViewArchived = (boardId: number) => {
    navigate(`/boards/${boardId}/archived`);
  };

  const handleOpenBoard = (board: Board) => {
    // Tableau verrouillé : ne pas ouvrir, afficher le message + lien vers Dépassements.
    if (board.locked) {
      setShowOverageLockedModal(true);
      return;
    }

    if (hasAds) {
      // Afficher la publicité interstitielle pour les utilisateurs du plan gratuit
      setPendingBoardId(board.id);
      setShowInterstitialAd(true);
    } else {
      // Accès direct pour les utilisateurs premium
      navigate(`/boards/${board.id}`);
    }
  };

  const handleManageOverages = () => {
    setShowOverageLockedModal(false);
    navigate('/account', { state: { activeTab: 'overages' } });
  };

  const handleCloseInterstitialAd = () => {
    setShowInterstitialAd(false);
    if (pendingBoardId) {
      navigate(`/boards/${pendingBoardId}`);
      setPendingBoardId(null);
    }
  };

  const getBoardStats = (board: Board) => {
    const totalCards = board.columns.reduce((sum, col) => sum + col.cards.length, 0);
    const activeCards = board.columns.reduce((sum, col) => 
      sum + col.cards.filter(card => !card.archived).length, 0);
    const archivedCards = board.columns.reduce((sum, col) => 
      sum + col.cards.filter(card => card.archived).length, 0);
    
    return { totalCards, activeCards, archivedCards };
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'role-badge role-super-admin';
      case 'admin':
        return 'role-badge role-admin';
      case 'editor':
        return 'role-badge role-editor';
      case 'visitor':
        return 'role-badge role-visitor';
      default:
        return 'role-badge';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Administrateur';
      case 'admin':
        return 'Administrateur';
      case 'editor':
        return 'Éditeur';
      case 'visitor':
        return 'Visiteur';
      default:
        return role;
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
    navigate('/login');
  };

  const getRoleBadge = (role: string) => {
    const badges = {
      super_admin: { label: 'Super Administrateur', color: '#9b59b6' },
      admin: { label: 'Administrateur', color: '#e74c3c' },
      editor: { label: 'Éditeur', color: '#3498db' },
      visitor: { label: 'Visiteur', color: '#95a5a6' },
    };
    return badges[role as keyof typeof badges] || badges.visitor;
  };

  const badge = user ? getRoleBadge(user.role) : { label: 'Visiteur', color: '#95a5a6' };

  const normalizedSearch = boardSearch.trim().toLowerCase();
  const filteredBoards = boards.filter(board => board.title.toLowerCase().includes(normalizedSearch));
  const ownedBoards = filteredBoards.filter(board => board.user_id === user?.id);
  const sharedBoards = filteredBoards.filter(board => board.user_id !== user?.id);

  const showOwnedBoards = boardFilter === 'all' || boardFilter === 'owned';
  const showSharedBoards = boardFilter === 'all' || boardFilter === 'shared';

  return (
    <div className="admin-page">
      {/* Navbar */}
      <div className="navbar">
        <div className="navbar-content">
          <div className="navbar-left">
            <h2 className="navbar-brand">Dashboard</h2>
          </div>
          <div className="navbar-right">
            {user && (
              <>
                {user.role === 'super_admin' && (
                  <button
                    className="btn btn-nav"
                    onClick={() => navigate('/admin/saas')}
                    style={{ backgroundColor: '#9b59b6', color: 'white', border: '2px solid #8e44ad' }}
                    title="Interface d'administration SaaS"
                  >
                    <ShieldCheck width={18} height={18} strokeWidth={2} />
                    <span>Admin SaaS</span>
                  </button>
                )}
                <div className="user-info" onClick={() => navigate('/account')} style={{ cursor: 'pointer' }} title="Paramètres du profil">
                  <div className="user-avatar">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="user-avatar-image" />
                    ) : (
                      user.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="user-details">
                    <div className="user-name">{user.name}</div>
                    <div className="user-role" style={{ backgroundColor: badge.color }}>
                      {badge.label}
                    </div>
                  </div>
                </div>
                <button className="btn btn-logout" onClick={handleLogout} title="Déconnexion">
                  <LogOut width={18} height={18} strokeWidth={2} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {accessDeniedMessage && (
        <div className="error-message" style={{ margin: '20px' }}>
          {accessDeniedMessage}
        </div>
      )}

      {error && (
        <div className="error-message" style={{ margin: '20px' }}>
          {error}
        </div>
      )}

      <div className="admin-content">
        {/* Invitations en attente */}
        <PendingInvitations />

        {/* Bannière publicitaire */}
        <AdBanner position="top" />

        {/* Bannière upgrade premium */}
        <PremiumUpgradeBanner />

        {/* Recherche, filtre et création */}
        <div className="admin-boards-header boards-main-toolbar">
          <div className="boards-toolbar">
            <input
              type="text"
              value={boardSearch}
              onChange={(e) => setBoardSearch(e.target.value)}
              placeholder="Rechercher un tableau..."
              className="boards-search-input"
            />

            <div className="boards-filter-dropdown" ref={filterDropdownRef}>
              <button
                type="button"
                className="boards-filter-button"
                onClick={() => setIsFilterMenuOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={isFilterMenuOpen}
              >
                <span>{BOARD_FILTER_OPTIONS.find((option) => option.value === boardFilter)?.label}</span>
                <NavArrowDown
                  width={16}
                  height={16}
                  strokeWidth={2}
                  className={`boards-filter-button-arrow ${isFilterMenuOpen ? 'open' : ''}`}
                />
              </button>

              {isFilterMenuOpen && (
                <ul className="boards-filter-menu" role="listbox">
                  {BOARD_FILTER_OPTIONS.map((option) => (
                    <li
                      key={option.value}
                      role="option"
                      tabIndex={0}
                      aria-selected={boardFilter === option.value}
                      className={`boards-filter-option ${boardFilter === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        setBoardFilter(option.value);
                        setIsFilterMenuOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setBoardFilter(option.value);
                          setIsFilterMenuOpen(false);
                        }
                      }}
                    >
                      {option.label}
                      {boardFilter === option.value && (
                        <Check width={16} height={16} strokeWidth={2} />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="create-board-inline">
            <input
              type="text"
              value={newBoardTitle}
              onChange={(e) => setNewBoardTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateBoard();
              }}
              placeholder="Créer un nouveau tableau..."
              className="create-board-input-inline"
              disabled={isCreating}
            />

            <button
              className="btn btn-primary"
              onClick={handleCreateBoard}
              disabled={isCreating || !newBoardTitle.trim()}
            >
              {isCreating ? 'Création...' : 'Créer'}
            </button>
          </div>
        </div>

        {showOwnedBoards && (
          <>
          <h2>Mes tableaux ({ownedBoards.length})</h2>
          {/* Grille des tableaux */}
          <div className="admin-boards-list">
            {isLoading ? (
              <div className="loading-message">Chargement...</div>
            ) : ownedBoards.length === 0 ? (
              <div className="empty-message">
                {normalizedSearch
                  ? 'Aucun tableau ne correspond à votre recherche'
                  : 'Aucun tableau créé'}
              </div>
            ) : (
              <div className="boards-grid-responsive">
                {ownedBoards.map((board) => {
                  const stats = getBoardStats(board);
                  const isOwner = board.user_id === user?.id;
                  
                  return (
                    <div key={board.id} className={`board-row${board.locked ? ' board-row-locked' : ''}`}>
                      <div
                        className="board-row-color"
                        style={{ backgroundColor: board.background_color || '#00bf7e' }}
                      />

                      <div className="board-row-content">
                        <div className="board-row-header">
                          {editingBoardId === board.id ? (
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onBlur={() => handleSaveTitle(board.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveTitle(board.id);
                                if (e.key === 'Escape') handleCancelEdit();
                              }}
                              autoFocus
                              className="board-row-title-input"
                            />
                          ) : (
                            <h3 className="board-row-title">
                              {board.title}
                              {board.locked && (
                                <span className="board-row-lock-badge" title="Dépassement à régler">
                                  <Lock width={14} height={14} strokeWidth={2} />
                                  <span>Dépassement à régler</span>
                                </span>
                              )}
                            </h3>
                          )}

                          {isOwner && (
                            <div className="board-row-actions">
                              <button
                                className="btn-icon"
                                onClick={() => handleStartEdit(board)}
                                title="Renommer"
                              >
                                <EditPencil width={18} height={18} strokeWidth={2} />
                              </button>

                              <button
                                className="btn-color-badge"
                                onClick={() =>
                                  setShowColorPalette(
                                    showColorPalette === board.id ? null : board.id
                                  )
                                }
                                title="Changer la couleur"
                                style={{
                                  background: board.background_color
                                    ? getGradientFromColor(board.background_color)
                                    : '#00bf7e'
                                }}
                              />

                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteBoard(board.id)}
                                title="Supprimer"
                              >
                                <Trash width={18} height={18} strokeWidth={2} />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <div className="board-row-stats">
                          <div className="board-stat">
                            <span className="board-stat-label">Cartes actives:</span>
                            <span className="board-stat-value">{stats.activeCards}</span>
                          </div>
                          <div className="board-stat">
                            <span className="board-stat-label">Cartes archivées:</span>
                            <span className="board-stat-value board-stat-archived">{stats.archivedCards}</span>
                          </div>
                          <div className="board-stat">
                            <span className="board-stat-label">Total:</span>
                            <span className="board-stat-value">{stats.totalCards}</span>
                          </div>
                          <div className="board-stat">
                            <span className="board-stat-label">Membres:</span>
                            <span className="board-stat-value">{board.members_count ?? '...'}</span>
                          </div>
                        </div>
                        
                        <div className="board-row-buttons">
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleOpenBoard(board)}
                          >
                            {board.locked ? (
                              <>
                                <Lock width={18} height={18} strokeWidth={2} />
                                <span>Dépassement à régler</span>
                              </>
                            ) : (
                              <>
                                <NavArrowRight width={18} height={18} strokeWidth={2} />
                                <span>Ouvrir</span>
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleViewArchived(board.id)}
                            disabled={stats.archivedCards === 0 || board.locked}
                          >
                            <BoxIso width={18} height={18} strokeWidth={2} />
                            <span>Cartes archivées</span>
                          </button>
                        </div>
                        
                        {/* Palette de couleurs */}
                        {showColorPalette === board.id && (
                          <div className="board-row-color-palette">
                            <h4>Choisir une couleur</h4>
                            <div className="color-palette-grid">
                              {/* Option pour réinitialiser à la couleur par défaut */}
                              <button
                                className={`color-option color-option-reset ${!board.background_color ? 'selected' : ''}`}
                                onClick={() => {
                                  handleUpdateColor(board.id, '');
                                  setShowColorPalette(null);
                                }}
                                title="Couleur par défaut (vert)"
                              >
                                <Xmark width={18} height={18} strokeWidth={2} />
                              </button>
                              {COLORS.map((color) => (
                                <button
                                  key={color}
                                  className={`color-option ${board.background_color === color ? 'selected' : ''}`}
                                  style={{ background: getGradientFromColor(color) }}
                                  onClick={() => {
                                    handleUpdateColor(board.id, color);
                                    setShowColorPalette(null);
                                  }}
                                  title={color}
                                >
                                  {board.background_color === color && (
                                    <span className="color-check">
                                      <Check width={18} height={18} strokeWidth={2} />
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </>
        )}
      </div>

      {showSharedBoards && sharedBoards.length > 0 && (
        <div className="admin-content">
          <div className="admin-boards-header">
            <h2>Tableaux partagés avec moi ({sharedBoards.length})</h2>
          </div>

          <div className="admin-boards-list">
            <div className="boards-grid-responsive">
              {sharedBoards.map((board) => {
                const stats = getBoardStats(board);

                return (
                  <div
                    key={board.id}
                    className={`board-row${board.locked ? ' board-row-locked' : ''}`}
                  >
                    <div
                      className="board-row-color"
                      style={{ backgroundColor: board.background_color || '#00bf7e' }}
                    />

                    <div className="board-row-content">
                      <div className="board-row-header">
                        <h3 className="board-row-title">
                          {board.title}

                          {board.locked && (
                            <span
                              className="board-row-lock-badge"
                              title="Dépassement à régler"
                            >
                              <Lock width={14} height={14} strokeWidth={2} />
                              <span>Dépassement à régler</span>
                            </span>
                          )}
                        </h3>
                        {board.owner_name && (
                          <div className="board-shared-info">
                            <span>Appartient à {board.owner_name}</span>
                          </div>
                        )}
                      </div>

                      <div className="board-row-stats">
                        <div className="board-stat">
                          <span className="board-stat-label">Cartes actives:</span>
                          <span className="board-stat-value">{stats.activeCards}</span>
                        </div>

                        <div className="board-stat">
                          <span className="board-stat-label">Cartes archivées:</span>
                          <span className="board-stat-value board-stat-archived">
                            {stats.archivedCards}
                          </span>
                        </div>

                        <div className="board-stat">
                          <span className="board-stat-label">Total:</span>
                          <span className="board-stat-value">{stats.totalCards}</span>
                        </div>

                        <div className="board-stat">
                          <span className="board-stat-label">Membres:</span>
                          <span className="board-stat-value">
                            {board.members_count ?? '...'}
                          </span>
                        </div>
                      </div>

                      <div className="board-row-buttons">
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleOpenBoard(board)}
                        >
                          {board.locked ? (
                            <>
                              <Lock width={18} height={18} strokeWidth={2} />
                              <span>Dépassement à régler</span>
                            </>
                          ) : (
                            <>
                              <NavArrowRight width={18} height={18} strokeWidth={2} />
                              <span>Ouvrir</span>
                            </>
                          )}
                        </button>

                        <button
                          className="btn btn-secondary"
                          onClick={() => handleViewArchived(board.id)}
                          disabled={stats.archivedCards === 0 || board.locked}
                        >
                          <BoxIso width={18} height={18} strokeWidth={2} />
                          <span>Cartes archivées</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Publicité interstitielle pleine page */}
      {showInterstitialAd && (
        <InterstitialAd onClose={handleCloseInterstitialAd} />
      )}

    <PremiumLimitModal
      isOpen={showPremiumLimitModal}
      onClose={() => setShowPremiumLimitModal(false)}
      message={
        <>
          Vous avez atteint la limite des <strong>3 tableaux</strong> inclus dans votre offre gratuite.
        </>
      }
      premiumMessage={
        <>
          Découvrez nos <strong>offres Premium</strong> pour créer jusqu'à <strong>10 tableaux</strong>,
          supprimer les publicités et profiter de fonctionnalités supplémentaires.
        </>
      }
    />

    {showOverageLockedModal && (
      <div className="modal-overlay">
        <div className="modal">
          <h2>Tableau verrouillé</h2>
          <p>
            Ce tableau dépasse la limite de <strong>10 tableaux</strong> incluse dans votre offre Premium.
            Réglez un dépassement pour le débloquer.
          </p>
          <div className="modal-actions">
            <button className="btn btn-primary" onClick={handleManageOverages}>
              Gérer mes dépassements
            </button>
            <button className="btn btn-secondary" onClick={() => setShowOverageLockedModal(false)}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    )}

    <ConfirmModal
      isOpen={boardToDeleteId !== null}
      title="Supprimer le tableau"
      message="Êtes-vous sûr de vouloir supprimer ce tableau ? Cette action est irréversible."
      confirmText="Supprimer"
      onConfirm={handleConfirmDeleteBoard}
      onCancel={() => setBoardToDeleteId(null)}
      variant="danger"
    />

    <ConfirmModal
      isOpen={showLogoutConfirm}
      title="Déconnexion"
      message="Voulez-vous vraiment vous déconnecter ?"
      confirmText="Se déconnecter"
      onConfirm={handleConfirmLogout}
      onCancel={() => setShowLogoutConfirm(false)}
    />

    </div>
  );
};
